/**
 * `NotificationsSource` over the M5 api. Structurally typed so this module never imports
 * server code (the same trick `modules/item-card/trpc-source.ts` uses).
 */
import type {
  BoardMuteView,
  InboxFacets,
  InboxRowView,
  NotificationPage,
  NotificationsSource,
  SettingsRowView,
} from './types';

interface Query<TIn, TOut> {
  query(input: TIn): Promise<TOut>;
}
interface Mutate<TIn, TOut> {
  mutate(input: TIn): Promise<TOut>;
}

export interface NotificationsTrpc {
  notifications: {
    list: Query<{ tab: string; unreadOnly: boolean; query?: string; cursor?: string }, NotificationPage>;
    markRead: Mutate<{ ids: string[]; read: boolean }, number>;
    markAllRead: Mutate<undefined, number>;
    delete: Mutate<{ ids: string[] }, number>;
    deleteAll: Mutate<undefined, number>;
    settings: Query<{ scope: 'User' } | undefined, SettingsRowView[]>;
    updateSetting: Mutate<{ kind: string; channel: string; enabled: boolean }, unknown>;
    boardMutes: Query<undefined, BoardMuteView[]>;
    setBoardMute: Mutate<{ boardId: number; muteState: string; enabled: string[] }, unknown>;
  };
  inbox: {
    feed: Query<
      { tab: string; boardIds?: number[]; unreadOnly: boolean; cursor?: string },
      { rows: InboxRowView[]; nextCursor: string | null }
    >;
    facets: Query<undefined, InboxFacets>;
    markRead: Mutate<{ updateIds: string[] }, number>;
    markAllRead: Mutate<{ boardIds?: number[] }, number>;
  };
}

export function createNotificationsSource(trpc: NotificationsTrpc): NotificationsSource {
  return {
    list: (input) =>
      trpc.notifications.list.query({
        tab: input.tab,
        unreadOnly: input.unreadOnly,
        ...(input.query ? { query: input.query } : {}),
        ...(input.cursor ? { cursor: input.cursor } : {}),
      }),
    markRead: async (ids, read = true) => {
      await trpc.notifications.markRead.mutate({ ids, read });
    },
    markAllRead: async () => {
      await trpc.notifications.markAllRead.mutate(undefined);
    },
    remove: async (ids) => {
      await trpc.notifications.delete.mutate({ ids });
    },
    removeAll: async () => {
      await trpc.notifications.deleteAll.mutate(undefined);
    },
    settings: () => trpc.notifications.settings.query({ scope: 'User' }),
    updateSetting: async (input) => {
      await trpc.notifications.updateSetting.mutate(input);
    },
    boardMutes: () => trpc.notifications.boardMutes.query(undefined),
    setBoardMute: async (input) => {
      await trpc.notifications.setBoardMute.mutate({
        boardId: Number(input.boardId),
        muteState: input.muteState,
        enabled: input.enabled,
      });
    },
    inboxFeed: (input) =>
      trpc.inbox.feed.query({
        tab: input.tab,
        unreadOnly: input.unreadOnly,
        ...(input.boardIds ? { boardIds: input.boardIds.map(Number) } : {}),
        ...(input.cursor ? { cursor: input.cursor } : {}),
      }),
    inboxFacets: () => trpc.inbox.facets.query(undefined),
    inboxMarkRead: async (updateIds) => {
      await trpc.inbox.markRead.mutate({ updateIds });
    },
    // Always an object: `undefined` has no JSON form, so it reached the server as `null` and the
    // `.optional()` input answered 400 (P-191). The server now accepts `null` too, belt and braces.
    inboxMarkAllRead: async (boardIds) => {
      await trpc.inbox.markAllRead.mutate(boardIds ? { boardIds: boardIds.map(Number) } : {});
    },
  };
}
